import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Box } from '@mui/material';
import { sanitizeHtmlForEditor } from './chatUtils';

interface ChatEditorProps {
  onSend: () => void;
  onContentChange: (text: string, html: string) => void;
  onMentionQuery: (query: string | null) => void;
  onPasteFiles?: (files: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
  clearTrigger?: number; // increment to clear editor
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const ChatEditor: React.FC<ChatEditorProps> = ({
  onSend,
  onContentChange,
  onMentionQuery,
  onPasteFiles,
  disabled = false,
  placeholder = 'Type a message... Use @ to mention',
  maxRows = 4,
  clearTrigger = 0,
  startAdornment,
  endAdornment,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Table,
      TableRow,
      TableCell,
      TableHeader,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    editable: !disabled,
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          onSend();
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // If HTML is present (e.g. Excel, Google Sheets copy), let TipTap handle it
        // so the table is pasted as rich content rather than the image thumbnail.
        if (clipboardData.types.includes('text/html')) return false;

        const imageFiles: File[] = [];
        for (let i = 0; i < clipboardData.items.length; i++) {
          const item = clipboardData.items[i];
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) imageFiles.push(file);
          }
        }

        if (imageFiles.length > 0 && onPasteFiles) {
          event.preventDefault();
          onPasteFiles(imageFiles);
          return true;
        }

        return false; // let other paste handling (HTML/text) proceed
      },
      transformPastedHTML: (html) => sanitizeHtmlForEditor(html),
    },
    onUpdate: ({ editor: ed }) => {
      const text = ed.getText();
      const html = ed.getHTML();
      onContentChange(text, html);

      // Detect @ mention trigger
      const { from } = ed.state.selection;
      const textBefore = ed.state.doc.textBetween(0, from, '\n');
      const atMatch = textBefore.match(/@(\w*)$/);
      if (atMatch) {
        onMentionQuery(atMatch[1].toLowerCase());
      } else {
        onMentionQuery(null);
      }
    },
  });

  // Clear editor when clearTrigger changes
  useEffect(() => {
    if (clearTrigger > 0 && editor) {
      editor.commands.clearContent();
    }
  }, [clearTrigger, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  const insertMention = useCallback((name: string) => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const textBefore = editor.state.doc.textBetween(0, from, '\n');
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch && atMatch.index !== undefined) {
      const docText = editor.state.doc.textBetween(0, from, '\n');
      const atPos = from - (docText.length - atMatch.index);
      editor.chain().focus().deleteRange({ from: atPos, to: from }).insertContent(`@${name} `).run();
    }
  }, [editor]);

  // Expose insertMention via a ref-like pattern on the DOM element
  useEffect(() => {
    const el = document.querySelector('.chat-editor-root');
    if (el) (el as any).__insertMention = insertMention;
  }, [insertMention]);

  const lineHeight = 20;
  const maxHeight = lineHeight * maxRows + 16;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      border: '1px solid', borderColor: 'grey.300', borderRadius: 6,
      bgcolor: disabled ? 'grey.100' : 'grey.50',
      '&:focus-within': { borderColor: 'grey.400' },
      width: '100%', overflow: 'hidden',
      px: 0.5, py: 0.5,
      minHeight: 48,
    }}>
      {startAdornment && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{startAdornment}</Box>
      )}
      <Box className="chat-editor-root" sx={{
        flex: 1, minWidth: 0, overflow: 'hidden',
        '& .tiptap': {
          outline: 'none',
          px: 1, py: 0.5,
          fontSize: 14, lineHeight: `${lineHeight}px`,
          maxHeight, overflowY: 'auto', overflowX: 'hidden',
          wordBreak: 'break-word',
          '& p': { m: 0 },
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'grey.500',
            float: 'left', height: 0, pointerEvents: 'none',
          },
          '& table': {
            borderCollapse: 'collapse', fontSize: '0.85em', my: 0.5,
            width: '100%', display: 'block', overflowX: 'auto',
          },
          '& th, & td': {
            border: '1px solid', borderColor: 'grey.300',
            px: 1, py: 0.25, minWidth: 40,
          },
          '& th': { fontWeight: 600, bgcolor: 'grey.100' },
          '& strong': { fontWeight: 600 },
          '& em': { fontStyle: 'italic' },
          '& ul, & ol': { pl: 2.5, my: 0.25 },
          '& code': { fontFamily: 'monospace', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.9em' },
          '& pre': { bgcolor: 'grey.100', p: 1, borderRadius: 1, my: 0.5, '& code': { bgcolor: 'transparent' } },
          '& blockquote': { borderLeft: '3px solid', borderColor: 'grey.400', pl: 1.5, ml: 0, my: 0.25, opacity: 0.8 },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>
      {endAdornment && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{endAdornment}</Box>
      )}
    </Box>
  );
};

export default ChatEditor;
